import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HardDriveDownload, Zap, Gauge, Save, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const NUM_FIELDS = [
  ["max_resolution", "Max optimized height (px)", "Cap the optimized media height. Never upscales."],
  ["crf", "Quality (CRF)", "Lower = higher quality/larger. 18–22 is visually lossless-ish."],
  ["keyframe_seconds", "Keyframe interval (s)", "Smaller = faster seeking, slightly larger files."],
  ["heavy_min_height", "Heavy: min source height", "Sources taller than this (vs output) are optimized."],
  ["heavy_bitrate_mbps", "Heavy: bitrate threshold (Mbps)", "Sources above this bitrate are optimized."],
  ["heavy_min_size_mb", "Heavy: file-size threshold (MB)", "Sources above this size are optimized."],
  ["timeout_s", "Optimization timeout (s)", "Hard cap for a single optimization."],
];
const BOOL_FIELDS = [
  ["enabled", "Pipeline enabled"],
  ["auto_optimize", "Auto-optimize heavy/problematic sources"],
  ["generate_proxy", "Generate 720p preview proxy"],
  ["vfr_normalize", "Normalize variable frame rate"],
  ["retain_original", "Always keep the original source"],
];
const PRESETS = ["ultrafast", "superfast", "veryfast", "faster", "fast", "medium", "slow"];

const Chip = ({ kind }) => {
  const map = { editor_friendly: ["Editor-friendly", "#22c55e"], heavy: ["Heavy", "#f59e0b"], problematic: ["Problematic", "#ef4444"] };
  const [label, color] = map[kind] || [kind || "—", "#A1A1AA"];
  return <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color, background: `${color}22` }} data-testid={`media-class-${kind}`}>{label}</span>;
};

export default function MediaPipeline() {
  const [settings, setSettings] = useState(null);
  const [diag, setDiag] = useState(null);
  const [ben, setBen] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, d, b] = await Promise.all([api.adminMediaSettings(), api.adminMediaDiagnostics(50), api.adminMediaBenchmarks()]);
      setSettings(s.settings); setDiag(d); setBen(b.benchmarks || []);
    } catch { toast.error("Couldn't load media pipeline data."); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { const r = await api.adminSaveMediaSettings(settings); setSettings(r.settings); toast.success("Media settings saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  if (!settings) return <div className="p-6 text-[#A1A1AA]" data-testid="media-pipeline-loading">Loading media pipeline…</div>;
  const roll = diag?.rollups || {};

  return (
    <div className="space-y-6" data-testid="media-pipeline">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-bold text-white"><HardDriveDownload size={20} className="text-[#00F0FF]" /> Optimized Media Pipeline</h2>
        <p className="mt-1 text-sm text-[#A1A1AA]">Analyze uploads once, build a fast high-quality editing intermediate, cache it, and reuse it everywhere. The original upload is never modified.</p>
      </div>

      {/* rollups */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[["Ingested", roll.total ?? 0], ["Optimized", roll.optimized_count ?? 0], ["Saved (MB)", roll.total_saved_mb ?? 0], ["Cached assets", roll.cached_optimized_assets ?? 0]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`media-rollup-${l}`}>
            <div className="text-2xl font-bold text-white">{v}</div><div className="text-xs text-[#A1A1AA]">{l}</div>
          </div>
        ))}
      </div>

      {/* settings */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold text-white"><Gauge size={16} className="text-[#8B5CF6]" /> Media Settings</h3>
          <div className="flex gap-2">
            <button onClick={load} data-testid="media-refresh-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-white"><RefreshCw size={13} /> Refresh</button>
            <button onClick={save} disabled={saving} data-testid="media-save-btn" className="flex items-center gap-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-semibold text-black hover:brightness-110 disabled:opacity-50"><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
        <div className="mb-4 flex flex-wrap gap-4">
          {BOOL_FIELDS.map(([k, l]) => (
            <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-[#E4E4E7]">
              <input type="checkbox" checked={!!settings[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`media-toggle-${k}`} className="h-4 w-4 accent-[#00F0FF]" />{l}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {NUM_FIELDS.map(([k, l, hint]) => (
            <label key={k} className="block">
              <span className="text-xs font-medium text-[#A1A1AA]">{l}</span>
              <input type="number" value={settings[k]} onChange={(e) => set(k, +e.target.value)} data-testid={`media-num-${k}`}
                className="mt-1 w-full rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]" />
              <span className="mt-0.5 block text-[10px] text-[#666]">{hint}</span>
            </label>
          ))}
          <label className="block">
            <span className="text-xs font-medium text-[#A1A1AA]">Encoder preset (speed/size)</span>
            <select value={settings.preset} onChange={(e) => set("preset", e.target.value)} data-testid="media-preset"
              className="mt-1 w-full rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]">
              {PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <span className="mt-0.5 block text-[10px] text-[#666]">Faster = quicker ingest, slightly larger files.</span>
          </label>
        </div>
      </div>

      {/* benchmarks */}
      {ben.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="media-benchmarks">
          <h3 className="mb-3 flex items-center gap-2 font-semibold text-white"><Zap size={16} className="text-[#FFC53D]" /> Raw vs Optimized Benchmark</h3>
          {ben.slice(0, 1).map((run) => (
            <div key={run.id}>
              <div className="mb-2 text-xs text-[#A1A1AA]">{run.label} · {new Date(run.created_at).toLocaleString()}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="text-[#A1A1AA]"><tr>
                    <th className="py-1.5 pr-3">Case</th><th className="py-1.5 pr-3">Original</th><th className="py-1.5 pr-3">Optimized</th>
                    <th className="py-1.5 pr-3">Optimize time</th><th className="py-1.5 pr-3">Decode</th><th className="py-1.5">Seek</th>
                  </tr></thead>
                  <tbody className="text-white">
                    {(run.cases || []).map((c, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-1.5 pr-3">{c.case}</td>
                        <td className="py-1.5 pr-3">{c.orig_size_mb} MB</td>
                        <td className="py-1.5 pr-3 text-[#22c55e]">{c.opt_size_mb} MB</td>
                        <td className="py-1.5 pr-3">{c.optimize_seconds}s</td>
                        <td className="py-1.5 pr-3 text-[#00F0FF]">{c.decode_speedup}</td>
                        <td className="py-1.5 text-[#00F0FF]">{c.seek_speedup}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {run.note && <p className="mt-2 text-[10px] text-[#666]">{run.note}</p>}
            </div>
          ))}
        </div>
      )}

      {/* diagnostics */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="media-diagnostics">
        <h3 className="mb-3 font-semibold text-white">Recent Media Ingestion</h3>
        {!diag?.recent?.length ? <p className="text-sm text-[#A1A1AA]">No media ingested yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-[#A1A1AA]"><tr>
                <th className="py-1.5 pr-3">File</th><th className="py-1.5 pr-3">Class</th><th className="py-1.5 pr-3">Original</th>
                <th className="py-1.5 pr-3">Optimized</th><th className="py-1.5 pr-3">Saved</th><th className="py-1.5">Time</th>
              </tr></thead>
              <tbody className="text-white">
                {diag.recent.map((r, i) => {
                  const o = r.original || {}, op = r.optimized;
                  const saved = op && o.size_mb ? Math.max(0, o.size_mb - op.size_mb).toFixed(1) : "—";
                  return (
                    <tr key={i} className="border-t border-white/5 align-top">
                      <td className="py-1.5 pr-3 max-w-[160px] truncate">{r.filename || "—"}</td>
                      <td className="py-1.5 pr-3"><Chip kind={r.media_class} />{r.reasons?.length ? <div className="mt-0.5 text-[10px] text-[#666]">{r.reasons.join(", ")}</div> : null}</td>
                      <td className="py-1.5 pr-3">{o.vcodec} {o.width}×{o.height} {o.fps}fps<div className="text-[10px] text-[#666]">{o.pix_fmt} {o.bit_depth}bit {o.bitrate_mbps}Mbps {o.size_mb}MB{o.vfr ? " · VFR" : ""}{o.hdr ? " · HDR" : ""}</div></td>
                      <td className="py-1.5 pr-3">{op ? <span className="text-[#22c55e]">{op.vcodec} {op.width}×{op.height} {op.fps}fps<div className="text-[10px] text-[#666]">{op.size_mb}MB</div></span> : <span className="text-[#A1A1AA]">not needed</span>}</td>
                      <td className="py-1.5 pr-3 text-[#22c55e]">{saved !== "—" ? `${saved} MB` : "—"}</td>
                      <td className="py-1.5">{r.optimize_seconds ? `${r.optimize_seconds}s` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
